import React, { useEffect, useState, useRef } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { Loader2, MapPin, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Define libraries array outside component to prevent unnecessary re-renders
const libraries: ("places")[] = ["places"];

interface PlacesAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  onPlaceSelected?: (place: google.maps.places.PlaceResult) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  label?: string;
  required?: boolean;
  clearable?: boolean;
  countryCode?: string | null; // ISO 3166-1 alpha-2 country code for geocoding restrictions
}

const PlacesAutocompleteInput: React.FC<PlacesAutocompleteInputProps> = ({
  value,
  onChange,
  placeholder,
  onPlaceSelected,
  disabled = false,
  className = "",
  id,
  label,
  required = false,
  clearable = true,
  countryCode = null // Default to no country restriction (global search)
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const [placeSelected, setPlaceSelected] = useState(false);
  const [isGeocodingInProgress, setIsGeocodingInProgress] = useState(false);
  const { t } = useLanguage();
  
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const autocompleteListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const isSettingUpAutocomplete = useRef<boolean>(false);
  const uniqueIdRef = useRef<string>(`autocomplete-${Math.random().toString(36).substring(2, 11)}`);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Get Google Maps API key from environment
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
  
  // Load Google Maps API with places library - reused for all instances
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey,
    libraries
  });

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Handle changes to the input field - update both local state and parent
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue); // Update parent as user types
    setPlaceSelected(false); // Reset place selected flag when user types
  };

  // Clear input
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInputValue('');
    onChange(''); // Call parent onChange to clear the address
    setPlaceSelected(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle geocoding when input field loses focus
  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    // Clear any existing blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    
    // Add longer delay to allow autocomplete place_changed event to process first
    blurTimeoutRef.current = setTimeout(async () => {
      try {
        await performGeocodingIfNeeded();
      } finally {
        blurTimeoutRef.current = null;
      }
    }, 500); // 500ms delay to ensure autocomplete has time to process
  };

  // Separate function for geocoding logic
  const performGeocodingIfNeeded = async () => {
    // Skip if place already selected or input is empty or geocoding is in progress
    if (placeSelected || !inputValue.trim() || isGeocodingInProgress || !isLoaded) return;
    
    // If no geocoder is available, skip
    if (!geocoderRef.current) {
      console.warn('Geocoder not initialized, cannot geocode address');
      return;
    }
    
    try {
      setIsGeocodingInProgress(true);
      setIsLoading(true);
      
      console.log(`Attempting to geocode manually entered address: "${inputValue}"`);
      
      // Perform geocoding
      const geocodeRequest: google.maps.GeocoderRequest = {
        address: inputValue,
        // Apply country restriction if countryCode is provided
        ...(countryCode ? { componentRestrictions: { country: countryCode } } : {})
      };
      
      const result = await geocoderRef.current.geocode(geocodeRequest);
      
      if (result.results && result.results.length > 0) {
        const firstResult = result.results[0];
        
        console.log('Geocoding successful, found location:', firstResult.formatted_address);
        
        // Create a place result that mimics what would come from autocomplete
        if (firstResult.geometry && firstResult.geometry.location) {
          // Format the place object to match what onPlaceSelected expects
          const geocodedPlace = {
            formatted_address: firstResult.formatted_address,
            geometry: {
              location: firstResult.geometry.location
            },
            place_id: firstResult.place_id,
            name: firstResult.formatted_address
          } as google.maps.places.PlaceResult;
          
          // Update state with formatted address
          setInputValue(firstResult.formatted_address);
          onChange(firstResult.formatted_address);
          
          // Call the onPlaceSelected callback if provided
          if (onPlaceSelected) {
            onPlaceSelected(geocodedPlace);
          }
          
          // Set place as selected
          setPlaceSelected(true);
        }
      } else {
        console.log('Geocoding returned no results for:', inputValue);
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
    } finally {
      setIsGeocodingInProgress(false);
      setIsLoading(false);
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Reset place selected state after paste
    setPlaceSelected(false);
  };

  // Cleanup function to properly remove autocomplete and listeners
  const cleanupAutocomplete = () => {
    // Remove the listener first if it exists
    if (autocompleteListenerRef.current) {
      google.maps.event.removeListener(autocompleteListenerRef.current);
      autocompleteListenerRef.current = null;
    }
    
    // Then clean up the autocomplete instance
    if (autocompleteRef.current) {
      // Remove all listeners associated with the autocomplete instance
      google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }
  };

  // Set up the autocomplete for the input field
  useEffect(() => {
    if (!isLoaded || !inputRef.current) {
      return;
    }
    
    
    // Prevent multiple simultaneous setups
    if (isSettingUpAutocomplete.current) return;
    isSettingUpAutocomplete.current = true;
    
    try {
      setIsLoading(true);
      
      // Clean up any existing autocomplete instance
      cleanupAutocomplete();
      
      // Create an autocomplete widget with proper types
      const options: google.maps.places.AutocompleteOptions = {
        types: ['geocode', 'establishment'], // Include both addresses and business establishments
        fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'],
        // Apply country restriction if countryCode is provided
        ...(countryCode ? { componentRestrictions: { country: countryCode } } : {})
      };
      
      // Apply unique ID to prevent conflicts with multiple autocomplete instances
      if (inputRef.current.id === id) {
        // If no custom ID is provided or it matches the current one, use our generated unique ID
        inputRef.current.setAttribute('data-autocomplete-id', uniqueIdRef.current);
      }
      
      // Create a new autocomplete instance
      const autocomplete = new google.maps.places.Autocomplete(inputRef.current, options);
      autocompleteRef.current = autocomplete;
      
      // Add listener for place selection
      autocompleteListenerRef.current = autocomplete.addListener('place_changed', () => {
        if (!autocompleteRef.current) return;
        
        // Clear any pending blur timeout since autocomplete is processing
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        
        const place = autocompleteRef.current.getPlace();
        if (!place || !place.geometry) {
          console.warn('No place or geometry returned from autocomplete');
          return;
        }
        
        // Verify the place has geometry location
        if (!place.geometry.location) {
          console.warn('Place selected has no geometry location');
          return;
        }
        
        // Update local state
        setPlaceSelected(true);
        
        // Use formatted_address from the place object if available
        if (place.formatted_address) {
          setInputValue(place.formatted_address);
          onChange(place.formatted_address);
        }
        
        // Call the callback with the complete place object
        if (onPlaceSelected) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          console.log(`Place selected for "${placeholder}": ${place.formatted_address}`);
          console.log(`Coordinates: ${lat}, ${lng}`);
          
          onPlaceSelected(place);
        }
      });
    } catch (error) {
      console.error('Error setting up autocomplete:', error);
    } finally {
      setIsLoading(false);
      isSettingUpAutocomplete.current = false;
    }
    
    // Clean up function
    return () => {
      cleanupAutocomplete();
    };
  }, [isLoaded, countryCode]); // Added countryCode to dependencies so autocomplete resets when country changes

  // Add handler for component unmounting
  useEffect(() => {
    return () => {
      // Clear blur timeout on unmount
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
      cleanupAutocomplete();
    };
  }, []);

  // Initialize geocoder when Google Maps is loaded
  useEffect(() => {
    if (!isLoaded) return;
    
    // Initialize geocoder
    if (!geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }
    
    return () => {
      geocoderRef.current = null;
    };
  }, [isLoaded]);

  if (loadError) {
    console.error('Google Maps script loading error:', loadError);
    // Fallback to regular input
    return (
      <div className="w-full">
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
        )}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onChange(e.target.value);
          }}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
          id={id}
          required={required}
        />
      </div>
    );
  }

  return (
    <div className="w-full relative">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MapPin size={16} className="text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`pl-10 pr-8 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${className}`}
          disabled={disabled || !isLoaded}
          id={id || uniqueIdRef.current}
          autoComplete="off"
          required={required}
          aria-label={placeholder}
          data-autocomplete-id={uniqueIdRef.current}
        />
        {isLoading && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <Loader2 size={16} className="animate-spin text-gray-400" />
          </div>
        )}
        {clearable && inputValue && !isLoading && (
          <button
            type="button"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onClick={handleClear}
            aria-label="Clear input"
          >
            <X size={16} />
          </button>
        )}
      </div>
      {!isLoaded && (
        <div className="text-xs text-yellow-600 mt-1">
          {t('mapComponent.placesLoading') || "Loading Google Maps services..."}
        </div>
      )}
      {placeSelected && (
        <div className="text-xs text-green-600 mt-1 flex items-center">
          ✓ {t('mapComponent.locationSelected') || "Location successfully selected"}
        </div>
      )}
    </div>
  );
};

export default PlacesAutocompleteInput;